import './DatasetButtons.css';

import { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import clsx from 'clsx';
import Button from '../../Button/Button';
import ArrowIcon from '../../ArrowIcon/ArrowIcon';
import OverflowTriggerPopover from '../../OverflowTriggerPopover/OverflowTriggerPopover';
import { Dataset, ReferenceDatasetControl } from '../../../types/types';
import { getControlVisibility } from './utils/getControlVisibility';
import { getRowsToUse } from './utils/getRowsToUse';
import { getIcon } from './utils/getIcon';
import { getStableControlKey } from './utils/getStableControlKey';
import { isWideLabel } from './utils/isWideLabel';
import Modal from '../../Modals/Modal/Modal';
import Loading from '../../Loading/Loading';
import { useControlAction } from '../../../hooks/useControlAction/useControlAction';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { getButtonColorFromControlIcon } from '../../../utils/getButtonColorFromControlIcon';
import { computeVirtualRowsFromSelectionMode } from './utils/computeVirtualRowsFromSelectionMode';
import { useDatasetDataRes } from '../../../api/types/types';
import { computeOverflowFit } from '../../../utils/computeOverflowFit';
import { sumWithGaps } from '../../../utils/sumWithGaps';
import useResizeTick from '../../../hooks/useResizeTick';
import { DatasetButtonsOverflowState } from './types';

interface DatasetButtonsProps {
  refetchData: () => void;
  children?: ReactNode;
  referenceDatasetControls?: ReferenceDatasetControl[];
  selectedRows: any[];
  selectedVirtualRows?: number;
  currentRow: any;
  masterRow?: any;
  dataset: Dataset;
  data: any[] | undefined;
  datasetData: useDatasetDataRes;
  activeFilters: Record<string, unknown> | null;
  activeSorting: any[];
  /** Called whenever overflow fit converges so parents can react (e.g. cascade hiding logic). */
  onOverflowChange?: (state: DatasetButtonsOverflowState) => void;
}

const DatasetButtons = ({
  refetchData,
  children,
  referenceDatasetControls,
  selectedRows,
  selectedVirtualRows = 0,
  currentRow,
  masterRow,
  dataset,
  data,
  datasetData,
  activeFilters,
  activeSorting,
  onOverflowChange,
}: DatasetButtonsProps) => {
  const [fitState, setFitState] = useState<{
    key: string;
    hidden: number;
    measured: boolean;
  }>({ key: '', hidden: 0, measured: false });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const childrenInRowRef = useRef<HTMLDivElement | null>(null);
  const onOverflowChangeRef = useRef(onOverflowChange);
  onOverflowChangeRef.current = onOverflowChange;
  /** Width when all dataset controls are in the overflow trigger — parent pins as dataset-buttons-wrapper min-width. */
  const collapsedWidthRef = useRef(0);

  /** Unique per instance so multiple DatasetButtons (e.g. list + slave grid) do not share one DOM id. */
  const additionalFunctionsTriggerId = `dataset-additional-trigger-${useId().replace(/:/g, '')}`;

  const { t } = useTranslate();
  const { getControlAction, controlModal, isActionLoading } =
    useControlAction();
  // All controls are disabled while a reload locate flow (master or slave) runs.
  const { reloadLocateInProgress, slaveReloadLocateInProgress } =
    useSelectedRowsContext();
  const reloadLocateRunning =
    reloadLocateInProgress || slaveReloadLocateInProgress;

  /** Dataset action buttons for the current row/selection (visibility + labels from server). */
  const allButtons = useMemo(() => {
    if (!dataset.controls) {
      return [];
    }

    return dataset.controls
      .filter((control) => {
        return getControlVisibility(
          control,
          currentRow,
          selectedRows,
          referenceDatasetControls,
          !data || data.length === 0,
          masterRow
        );
      })
      .map((control) => {
        const computedSelectedVirtualRows = computeVirtualRowsFromSelectionMode(
          selectedVirtualRows,
          datasetData,
          data,
          control.selectionMode
        );
        const rowsToUse = getRowsToUse(
          control.selectionMode,
          selectedRows,
          currentRow,
          data
        );
        const action = getControlAction(
          activeFilters,
          activeSorting,
          dataset,
          data ?? [],
          datasetData?.totalCount,
          control,
          rowsToUse,
          currentRow,
          computedSelectedVirtualRows,
          refetchData
        );

        const icon = getIcon(control?.icon);
        const color = getButtonColorFromControlIcon(control?.icon);

        let label = control.label;
        if (referenceDatasetControls) {
          const foundReferenceControl = referenceDatasetControls.find(
            ({ name }) => name === control.name
          );
          if (foundReferenceControl?.label) {
            label = foundReferenceControl.label;
          }
        }
        const disabled =
          reloadLocateRunning ||
          (control.action?.type === 'aggregate' &&
            (!data || data.length === 0));

        return {
          key: control.name ?? control.label,
          label,
          hint: control.hint,
          icon,
          color,
          action,
          disabled,
        };
      });
  }, [
    currentRow,
    selectedRows,
    dataset,
    datasetData,
    data,
    masterRow,
    referenceDatasetControls,
    refetchData,
    getControlAction,
    reloadLocateRunning,
  ]);

  /** Fingerprint changes whenever anything affecting button widths changes (label, icon, color, order). */
  const controlsLayoutKey = useMemo(
    () =>
      allButtons
        .map(
          (button) =>
            `${button.key ?? ''} ${button.label ?? ''} ${
              button.hint ?? ''
            } ${button.icon ? '1' : '0'} ${button.color ?? ''}`
        )
        .join('|'),
    [allButtons]
  );

  /** Hide only on first mount or content (key) change. Resize-driven re-fits update hiddenCount silently — container overflow:hidden masks transient clipping, no repeated visibility flash during drag. */
  const uiLoading = !fitState.measured || fitState.key !== controlsLayoutKey;
  const hiddenCount = fitState.key === controlsLayoutKey ? fitState.hidden : 0;

  const resizeTick = useResizeTick(
    [containerRef, mirrorRef],
    controlsLayoutKey
  );

  /** Notify parent of overflow state so cascading hide logic can engage when dataset is fully collapsed. */
  useEffect(() => {
    if (!onOverflowChangeRef.current) return;
    if (!fitState.measured) return;
    if (fitState.key !== controlsLayoutKey) return;
    const total = allButtons.length;
    const hidden = fitState.hidden;
    onOverflowChangeRef.current({
      total,
      hidden,
      allHidden: total > 0 && hidden === total,
      collapsedWidth: collapsedWidthRef.current,
    });
  }, [fitState, controlsLayoutKey, allButtons.length]);

  /** Single-pass fit: measure mirror's per-button widths, compute hidden greedily. No iteration → no flash. */
  useEffect(() => {
    const containerElement = containerRef.current;
    const mirrorElement = mirrorRef.current;
    if (!containerElement || !mirrorElement) {
      return;
    }

    const containerWidth = containerElement.clientWidth;
    if (containerWidth === 0) {
      // Wait for ResizeObserver to fire when container gets a real width.
      return;
    }

    if (allButtons.length === 0) {
      setFitState((previous) =>
        previous.measured &&
        previous.key === controlsLayoutKey &&
        previous.hidden === 0
          ? previous
          : { key: controlsLayoutKey, hidden: 0, measured: true }
      );
      return;
    }

    const actionButtonElements = Array.from(
      mirrorElement.querySelectorAll<HTMLElement>('.dataset-action-button')
    );
    const triggerButtonElement = mirrorElement.querySelector<HTMLElement>(
      '.dataset-additional-buttons-trigger'
    );

    if (actionButtonElements.length !== allButtons.length) {
      // Mirror DOM not yet in sync with current button set — wait for next tick.
      return;
    }

    const buttonRects = actionButtonElements.map((element) =>
      element.getBoundingClientRect()
    );
    if (buttonRects.some((rect) => rect.width === 0)) {
      return;
    }

    const triggerWidth = triggerButtonElement
      ? triggerButtonElement.getBoundingClientRect().width
      : 0;
    const childrenInRowWidth = childrenInRowRef.current
      ? childrenInRowRef.current.getBoundingClientRect().width
      : 0;

    // Real flex gap from adjacent mirror children — covers flex gap + margins.
    let gap = 0;
    if (buttonRects.length >= 2) {
      gap = Math.max(0, buttonRects[1].left - buttonRects[0].right);
    } else if (buttonRects.length === 1 && triggerButtonElement) {
      gap = Math.max(
        0,
        triggerButtonElement.getBoundingClientRect().left - buttonRects[0].right
      );
    }

    const { hiddenHideable } = computeOverflowFit({
      hideableWidths: buttonRects.map((rect) => rect.width),
      fixedLeadingWidth: childrenInRowWidth,
      triggerWidth,
      gap,
      budget: containerWidth - 4,
    });
    const hidden = hiddenHideable;

    const collapsedItems: number[] = [];
    if (childrenInRowWidth > 0) collapsedItems.push(childrenInRowWidth);
    if (triggerWidth > 0) collapsedItems.push(triggerWidth);
    collapsedWidthRef.current = sumWithGaps(collapsedItems, gap);

    setFitState((previous) =>
      previous.measured &&
      previous.key === controlsLayoutKey &&
      previous.hidden === hidden
        ? previous
        : { key: controlsLayoutKey, hidden, measured: true }
    );
  }, [allButtons, controlsLayoutKey, resizeTick]);

  const visibleButtonsCount = Math.max(allButtons.length - hiddenCount, 0);
  const mainButtons = allButtons.slice(0, visibleButtonsCount);
  const additionalButtons = allButtons.slice(visibleButtonsCount);

  // Keep built-in header actions visible even when the dataset has no server controls.
  if (!dataset.controls && !referenceDatasetControls && !children) {
    return <></>;
  }

  return (
    <>
      <div className="dataset-buttons-container" ref={containerRef}>
        {/* Off-screen mirror with every action button + the overflow trigger.
            Provides stable per-element widths for one-shot fit calculation. */}
        <div
          className="dataset-buttons dataset-buttons-mirror"
          ref={mirrorRef}
          aria-hidden="true"
        >
          {allButtons.map((control) => (
            <Button
              key={`mirror-${getStableControlKey(control)}`}
              fill="clear"
              color={control?.color}
              className={clsx(
                'dataset-action-button',
                isWideLabel(control?.label) && 'dataset-action-button--wide'
              )}
              round={false}
            >
              {control?.icon && <IonIcon icon={control?.icon} slot="start" />}
              <span className="button-text">{control?.label}</span>
            </Button>
          ))}
          <Button
            className="dataset-additional-buttons-trigger"
            fill="clear"
            color="dark"
            round={false}
          >
            <ArrowIcon direction="down" color="dark" />
            {t('otherFunctions')}
          </Button>
        </div>

        <div
          className={clsx(
            'dataset-buttons',
            'dataset-buttons-visible',
            uiLoading && 'buttons-loading'
          )}
        >
          {referenceDatasetControls && (
            <div ref={childrenInRowRef} className="dataset-buttons-children">
              {children}
            </div>
          )}

          {mainButtons.map((control) => (
            <Button
              key={getStableControlKey(control)}
              dataAttributes={{ 'data-control-key': getStableControlKey(control) }}
              onClick={control?.action}
              fill="clear"
              color={control?.color}
              title={control?.hint}
              disabled={control?.disabled}
              className={clsx(
                'dataset-action-button',
                isWideLabel(control?.label) && 'dataset-action-button--wide'
              )}
              round={false}
            >
              {control?.icon && <IonIcon icon={control?.icon} slot="start" />}
              <span className="button-text">{control?.label}</span>
            </Button>
          ))}

          {additionalButtons.length > 0 && (
            <OverflowTriggerPopover
              triggerId={additionalFunctionsTriggerId}
              label={
                mainButtons.length === 0 ? t('functions') : t('otherFunctions')
              }
              triggerColor="dark"
              arrowColor="dark"
              disabled={reloadLocateRunning}
              items={additionalButtons.map((control) => ({
                key: getStableControlKey(control),
                label: control?.label ?? '',
                icon: control?.icon ?? undefined,
                onClick: control?.action,
                disabled: control?.disabled,
              }))}
            />
          )}
        </div>
      </div>

      {!referenceDatasetControls && (
        <div className="right-buttons">{children}</div>
      )}
      {controlModal.isModalOpen && <Modal {...controlModal.modalProps} />}
      {/* Overlay while bulk-fetching missing virtual rows (e.g. after select-all across pagination) */}
      {isActionLoading && <Loading />}
    </>
  );
};

export default DatasetButtons;
